import type { Command } from '@/core/Command';
import type { Terrain } from '@/terrain/Terrain';
import { isEmptyRect, type SurfaceRect } from '@/terrain/TerrainSurface';
import { readHeightRect, writeHeightRect } from '@/terrain/TerrainSculpt';

/**
 * Undo records for the terrain tools.
 *
 * ## One stroke, one undo step, one rectangle
 *
 * A stroke is hundreds of dabs, so recording a command per dab would bury an afternoon's planting
 * under a thousand history entries. A stroke is therefore captured whole: press, drag, release, one
 * entry.
 *
 * But "whole" cannot mean a whole grid. Snapshotting a 512×512 paint map costs a megabyte per
 * stroke, and the undo stack holds a hundred of them — a hundred megabytes to be able to take back
 * a two-metre smudge. So a stroke records only the rectangle it actually touched, before and after.
 * A brush is a local operation; its undo record should be too. In practice a stroke's diff is a few
 * kilobytes, and the pathological case (painting the entire site in one gesture) costs exactly what
 * the naive implementation would have cost for every stroke.
 *
 * The rectangle is accumulated by the tool as the union of every dab's own dirty rect, so it is
 * tight around the real edit rather than around the pointer path.
 */

/** Fired after a command changes the ground, so dependent state can be re-derived. */
export type TerrainChangeHook = (rect: SurfaceRect) => void;

/**
 * One paint stroke. Holds the RGBA bytes of the touched rectangle as they were and as they are.
 */
export class PaintStrokeCommand implements Command {
  readonly label: string;
  readonly kind = 'paint' as const;

  constructor(
    private readonly terrain: Terrain,
    private readonly rect: SurfaceRect,
    private readonly before: Uint8Array,
    private readonly after: Uint8Array,
    label = 'Paint ground',
    private readonly onChange?: TerrainChangeHook,
  ) {
    this.label = label;
  }

  /** Whether this stroke actually changed anything worth recording. */
  get isEmpty(): boolean {
    if (isEmptyRect(this.rect)) return true;
    if (this.before.length !== this.after.length) return false;
    for (let i = 0; i < this.before.length; i += 1) {
      if (this.before[i] !== this.after[i]) return false;
    }
    return true;
  }

  execute(): void {
    this.terrain.surface.writeRect(this.rect, this.after);
    this.terrain.applySurfaceRegion();
    this.onChange?.(this.rect);
  }

  undo(): void {
    this.terrain.surface.writeRect(this.rect, this.before);
    this.terrain.applySurfaceRegion();
    this.onChange?.(this.rect);
  }
}

/**
 * One sculpt stroke.
 *
 * Everything the ground carries — where each plant sits, where a shed's floor is — is *derived* from
 * the heights, never stored twice. So undo restores the heights and re-runs the same derivation the
 * stroke did, and every plant returns to the height it had. Recording the plants' own positions
 * alongside would work too, and would be a second copy of the truth waiting to disagree with the
 * first.
 */
export class SculptStrokeCommand implements Command {
  readonly label: string;
  readonly kind = 'sculpt' as const;

  constructor(
    private readonly terrain: Terrain,
    private readonly rect: SurfaceRect,
    private readonly before: Float32Array,
    private readonly after: Float32Array,
    label = 'Sculpt terrain',
    private readonly onChange?: TerrainChangeHook,
  ) {
    this.label = label;
  }

  get isEmpty(): boolean {
    if (isEmptyRect(this.rect)) return true;
    if (this.before.length !== this.after.length) return false;
    for (let i = 0; i < this.before.length; i += 1) {
      if (this.before[i] !== this.after[i]) return false;
    }
    return true;
  }

  execute(): void {
    this.write(this.after);
  }

  undo(): void {
    this.write(this.before);
  }

  private write(heights: Float32Array): void {
    writeHeightRect(this.terrain.field, this.rect, heights);
    this.terrain.applyHeightRegion(this.rect);
    this.terrain.finishHeightEdit();
    this.onChange?.(this.rect);
  }
}

/** Snapshot a heightfield rectangle — the "before" half of a {@link SculptStrokeCommand}. */
export function snapshotHeights(terrain: Terrain, rect: SurfaceRect): Float32Array {
  return readHeightRect(terrain.field, rect);
}
